/**
 * Translational and rotational dynamics for the 2D rocket landing simulator.
 *
 * Coordinate conventions (2D vertical plane, X/Z):
 *   World frame: X horizontal (right positive), Z vertical (up positive, ground = 0).
 *   Body frame: nose points along body x-axis; pitch theta measured from +Z toward +X.
 *   theta = 0  -> rocket points straight up.
 *   theta > 0  -> nose tilted toward +X.
 *   Positive gimbal deflects thrust toward +X relative to the body axis.
 *
 * Thrust direction (inertial):
 *   [sin(theta + gimbal), cos(theta + gimbal)]
 *
 * Torque about center of mass (Y axis, out of page):
 *   tau_y = -d * T * sin(gimbal)
 *   Positive gimbal -> negative torque -> nose rotates toward -X.
 *   This matches: engine mounted below CoM at (0, -d); thrust deflected +X pushes
 *   tail toward +X, rotating nose toward -X.
 */

import type { Environment, RocketParams, RocketState, ControlAction } from "./types";

/**
 * Derivative of the continuous state vector.
 *
 * The control action is assumed constant during a physics step. Fuel exhaustion
 * is handled by the integrator (step splitting), not by clamping here.
 *
 * @param state - Current simulation state.
 * @param action - Applied control action (already clamped to limits).
 * @param env - Environment parameters.
 * @param params - Rocket physical parameters.
 * @returns State derivatives [dx, dz, dvx, dvz, dfuel, dtheta, domega].
 */
export function computeDerivatives(
  state: RocketState,
  action: ControlAction,
  env: Environment,
  params: RocketParams
): {
  dx: number;
  dz: number;
  dvx: number;
  dvz: number;
  dfuel: number;
  dtheta: number;
  domega: number;
} {
  const mass = params.dryMass + state.fuel;
  const T = action.throttle * params.maxThrust;

  // Thrust vector in inertial frame.
  const thetaPlusGimbal = state.theta + action.gimbal;
  const thrustX = T * Math.sin(thetaPlusGimbal);
  const thrustZ = T * Math.cos(thetaPlusGimbal);

  // Gravity.
  const gravityX = 0;
  const gravityZ = -mass * env.gravity;

  // Drag.
  const speed = Math.sqrt(state.vx * state.vx + state.vz * state.vz);
  const rho = env.density(state.z);
  const dragFactor = -0.5 * rho * speed * speed * params.dragCoefficient * params.referenceArea;
  let dragX = 0;
  let dragZ = 0;
  if (speed > 0) {
    dragX = dragFactor * (state.vx / speed);
    dragZ = dragFactor * (state.vz / speed);
  }

  // Net force.
  const netFX = thrustX + gravityX + dragX;
  const netFZ = thrustZ + gravityZ + dragZ;

  // Translational derivatives.
  const dx = state.vx;
  const dz = state.vz;
  const dvx = netFX / mass;
  const dvz = netFZ / mass;

  // Fuel depletion.
  const dfuel = -params.fuelFlowRate * action.throttle;

  // Rotational derivatives.
  const torqueY = -params.centerOfMassOffset * T * Math.sin(action.gimbal);
  const dtheta = state.omega;
  const domega = torqueY / params.inertiaY;

  return { dx, dz, dvx, dvz, dfuel, dtheta, domega };
}
